import { HillPreview } from '@/components/hill-preview';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { hillsApi } from '@/lib/api';
import { formatRelativeTime } from '@/lib/format-relative-time';
import { queryKeys } from '@/lib/query-keys';
import type { HillChart } from '@hillchart/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ExternalLinkIcon, Trash2Icon } from 'lucide-react';
import { type MouseEvent, useRef, useState } from 'react';

/**
 * Display mode for the list. Drives:
 *  - empty state copy
 *  - primary CTA target (`/hills/$id` vs `/archive/$id`)
 *  - which timestamp the body shows ("Created on" vs "Done on")
 */
export type HillListMode = 'live' | 'archive';

interface HillListProps {
  hills: HillChart[];
  /** Defaults to `'live'` so existing call sites keep their current behavior. */
  mode?: HillListMode;
}

/**
 * Grid of hill cards.
 *
 * The whole card surface is clickable (stretched-link pattern): a single
 * `<Link>` covers the card body via absolute positioning, and the action
 * buttons in the footer (the upstream link, Delete) sit on a higher z-index
 * with `stopPropagation` to consume their own clicks. This matches the
 * common "clickable card" UX without trapping interactions on nested
 * controls.
 *
 * Deletion goes through an `<AlertDialog>` because it cascades to dots and
 * snapshots and is not reversible from the UI.
 */
export function HillList({ hills, mode = 'live' }: HillListProps) {
  if (hills.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 border-dashed bg-slate-50 px-8 py-16 text-center">
        <h2 className="font-semibold text-brand-900 text-lg">
          {mode === 'archive' ? 'No archived hills yet' : 'No hills yet'}
        </h2>
        <p className="mx-auto mt-2 max-w-md text-slate-600 text-sm">
          {mode === 'archive'
            ? 'Hills appear here once a teammate marks them as Done. Their snapshot history stays viewable in read-only mode.'
            : 'Import a GitLab project to turn its milestones into a hill chart. You\'ll be able to drag each milestone across the hill from "figuring it out" to "done".'}
        </p>
      </div>
    );
  }

  return (
    <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {hills.map((hill) => (
        <li key={hill.id}>
          <HillCard hill={hill} mode={mode} />
        </li>
      ))}
    </ul>
  );
}

interface HillCardProps {
  hill: HillChart;
  mode: HillListMode;
}

function HillCard({ hill, mode }: HillCardProps) {
  const qc = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);

  // `externalProjectUrl` points at GitLab on every hill imported since the
  // migration and at Linear only on legacy rows, so anything that names the
  // upstream system has to follow the provider rather than hardcode one.
  const providerName = hill.provider === 'linear' ? 'Linear' : 'GitLab';

  /**
   * Synchronous guard against double-submission.
   *
   * `deleteMutation.isPending` is a React state - it flips to `true` only at
   * the next render, so when Radix fires `onSelect` twice inside the same
   * event-loop tick (Enter held down, double-click, or an assistive tech
   * replaying the activation), both clicks see `isPending === false` and
   * each enqueues a `mutate()`. The first resolves 200; the subsequent ones
   * hit a now-deleted hill and return 404. A ref is the only thing that
   * flips synchronously within a handler.
   */
  const submittingRef = useRef(false);

  const deleteMutation = useMutation({
    mutationFn: () => hillsApi.delete(hill.id),
    onSuccess: () => {
      // Invalidate every status-keyed list (`hillsListPrefix` matches active
      // and done) so this card disappears from whichever surface displayed
      // it - the archive view shares the same delete path.
      qc.invalidateQueries({ queryKey: queryKeys.hillsListPrefix() });
      qc.invalidateQueries({ queryKey: queryKeys.gitlabProjects() });
      qc.removeQueries({ queryKey: queryKeys.hill(hill.id) });
      qc.removeQueries({ queryKey: queryKeys.snapshots(hill.id) });
      // Close the dialog explicitly. The card is about to unmount on the
      // next list refetch anyway, but if the refetch lags we don't want a
      // ghost dialog sitting on screen.
      setConfirmOpen(false);
    },
    onSettled: () => {
      submittingRef.current = false;
    },
  });

  const completedDots = hill.dots.filter((d) => d.completed).length;
  const isDone = hill.completedAt !== null;

  /**
   * Stop click propagation on action buttons so the stretched card link
   * doesn't fire alongside (e.g. clicking the upstream external-link button
   * would otherwise also navigate to the detail view).
   */
  const stopBubble = (e: MouseEvent) => {
    e.stopPropagation();
  };

  return (
    // `relative` anchors the absolutely-positioned stretched link.
    // `overflow-hidden` matters: the preview sits flush with the card
    // edges (no horizontal padding) and its background colour would
    // otherwise overflow the rounded corners on hover.
    <Card className="relative flex h-full flex-col overflow-hidden transition-shadow hover:shadow-md">
      {/*
        Stretched link covers the whole card. Sits at z-0 so it's behind
        the action buttons in the footer (which carry stopPropagation +
        their own z-index). `aria-label` gives screen readers context
        because the link text is empty - the visible card title above
        is what humans read.
      */}
      {mode === 'archive' ? (
        <Link
          to="/archive/$hillId"
          params={{ hillId: hill.id }}
          aria-label={`Open ${hill.title}`}
          className="absolute inset-0 z-0 cursor-pointer"
        />
      ) : (
        <Link
          to="/hills/$hillId"
          params={{ hillId: hill.id }}
          aria-label={`Open ${hill.title}`}
          className="absolute inset-0 z-0 cursor-pointer"
        />
      )}

      {/* Thumbnail sits above the title so the user can scan a grid of
          cards by shape alone. When the hill has no dots yet, we skip the
          preview entirely rather than show an empty curve. */}
      {hill.dots.length > 0 ? <HillPreview dots={hill.dots} /> : null}

      <CardHeader>
        <CardTitle className="line-clamp-2">{hill.title}</CardTitle>
        <CardDescription>
          {hill.dots.length} dot{hill.dots.length === 1 ? '' : 's'}
          {/*
            Hide "0 done" - it adds noise without information. Only show
            the completion count when at least one dot has actually moved.
          */}
          {completedDots > 0 ? ` · ${completedDots} done` : ''}
        </CardDescription>
      </CardHeader>

      <CardContent className="flex-1">
        {/*
          Active hills surface the creation date. Done hills show BOTH dates
          so the archive view tells a story: how long it ran, when it landed.
          Both use `formatRelativeTime` which scales from "just now" through
          "N minutes/hours/days ago" and falls back to an absolute date past
          a week — the previous helper bucketed everything into "today" or
          a date, which gave coarse output for hills created hours ago.
        */}
        <p className="text-slate-500 text-xs">Created {formatRelativeTime(hill.createdAt)}</p>
        {isDone && hill.completedAt !== null ? (
          <p className="mt-0.5 text-slate-500 text-xs">
            Done {formatRelativeTime(hill.completedAt)}
          </p>
        ) : null}
      </CardContent>

      {/*
        Footer carries the action buttons. `relative z-10` lifts the whole
        bar above the stretched link so the buttons capture their own
        clicks. Each button also calls `stopPropagation` for safety - the
        z-index alone is not sufficient if a parent listener lives on a
        higher ancestor.
      */}
      <CardFooter className="relative z-10 flex items-center justify-between gap-2">
        {/*
          The "Open" button is now redundant with the stretched card link,
          but we keep it as the explicit affordance for keyboard users
          (Tab → space/enter on this button is faster than locating the
          invisible stretched link).
        */}
        <Button asChild variant="default" size="sm" onClick={stopBubble}>
          {mode === 'archive' ? (
            <Link to="/archive/$hillId" params={{ hillId: hill.id }}>
              Open
            </Link>
          ) : (
            <Link to="/hills/$hillId" params={{ hillId: hill.id }}>
              Open
            </Link>
          )}
        </Button>

        <div className="flex items-center gap-1">
          {hill.externalProjectUrl ? (
            <Button
              asChild
              variant="ghost"
              size="icon"
              aria-label={`Open in ${providerName}`}
              onClick={stopBubble}
            >
              <a href={hill.externalProjectUrl} target="_blank" rel="noreferrer">
                <ExternalLinkIcon className="h-4 w-4" />
              </a>
            </Button>
          ) : null}

          <AlertDialog
            open={confirmOpen}
            onOpenChange={(next) => {
              // Block Escape / overlay-click dismissal while the DELETE is in
              // flight. Without this, the user can close the dialog mid-request
              // and be left wondering whether the deletion happened.
              if (deleteMutation.isPending) return;
              setConfirmOpen(next);
            }}
          >
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Delete hill"
                disabled={deleteMutation.isPending}
                onClick={stopBubble}
              >
                <Trash2Icon className="h-4 w-4 text-red-600" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this hill?</AlertDialogTitle>
                <AlertDialogDescription>
                  This removes the hill <strong>{hill.title}</strong>, every dot on it, and all
                  snapshots. The underlying {providerName} project is untouched. This action cannot
                  be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    // `e.preventDefault()` keeps the dialog open so we can
                    // show the "Deleting..." state; it closes programmatically
                    // in `onSuccess`.
                    e.preventDefault();
                    if (submittingRef.current) return;
                    submittingRef.current = true;
                    deleteMutation.mutate();
                  }}
                  disabled={deleteMutation.isPending}
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardFooter>
    </Card>
  );
}
